import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Sparkles } from 'lucide-react';

const STORAGE_KEY = 'gw_flex_final_reflections';

const SECTIONS = [
  {
    id: 'looking-back',
    title: 'Looking Back',
    questions: [
      'What surprised you the most during these 21 days?',
      'What was easier than you expected?',
      'What was more challenging than you expected?',
      'What was your biggest breakthrough?',
      "What did you learn about your body that you didn't know before?",
    ],
  },
  {
    id: 'body-awareness',
    title: 'Body Awareness',
    questions: [
      'Which symptom changed the most?',
      "Which symptom didn't change as much as you hoped?",
      'How would you describe your energy now compared to Day 1?',
      'How has your sleep changed?',
      'How has your digestion changed?',
      'Have you noticed changes in pain, stiffness, or inflammation?',
      'Has your mood or emotional resilience shifted?',
      'Do you recover from stress differently than before?',
    ],
  },
  {
    id: 'food-awareness',
    title: 'Food Awareness',
    questions: [
      'Which foods made you feel amazing?',
      'Which foods no longer feel worth it?',
      'Did you notice any food sensitivities?',
      'What surprised you about your appetite or cravings?',
      'Did your relationship with sugar, caffeine, or alcohol change?',
      'Which healthy habit felt most sustainable?',
    ],
  },
  {
    id: 'nervous-system',
    title: 'Nervous System',
    questions: [
      'Do you feel calmer in your body?',
      'How often do you notice yourself breathing deeply now?',
      'Did you become more aware of stress before it overwhelmed you?',
      'What helped regulate your nervous system the most?',
    ],
  },
  {
    id: 'identity-shift',
    title: 'Identity Shift',
    subtitle: 'My favorite section — these questions create lasting change.',
    questions: [
      'What kind of person are you becoming?',
      'What old version of yourself are you leaving behind?',
      'Finish this sentence: “I now trust my body because…”',
      'Finish this sentence: “My body has been trying to tell me…”',
      'What limiting belief did this detox challenge?',
      "What habit no longer fits the person you're becoming?",
    ],
  },
  {
    id: 'future-focus',
    title: 'Future Focus',
    questions: [
      'Which three habits are non-negotiable going forward?',
      'What will you continue daily?',
      'What will you continue weekly?',
      'What obstacle might pull you off track?',
      "What's your plan when life gets busy?",
      'If you could speak to yourself on Day 1, what advice would you give?',
    ],
  },
  {
    id: 'confidence-scale',
    title: 'Confidence Scale',
    subtitle: 'Rate each from 1–10.',
    questions: [
      'Energy',
      'Sleep',
      'Digestion',
      'Mood',
      'Mental clarity',
      'Stress resilience',
      'Pain',
      'Motivation',
      'Relationship with food',
      'Confidence in your health',
    ],
  },
  {
    id: 'mirror',
    title: 'The “Mirror” Questions',
    questions: [
      'What are you most pleased with within yourself?',
      'Where did you surprise yourself?',
      "What are you grateful your body continued to do for you, even when you weren't feeling your best?",
      'What does health mean to you now?',
      'If your body could write you a thank-you note, what would it say?',
    ],
  },
];

export const FinalReflections = () => {
  const [open, setOpen] = useState(null);
  const [entries, setEntries] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch { return {}; }
  });

  const setEntry = (id, val) => {
    setEntries((prev) => {
      const next = { ...prev, [id]: val };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  return (
    <section className="max-w-4xl mx-auto px-6 mt-12" data-testid="final-reflections-section">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 mb-2">
          <Sparkles size={16} className="text-terracotta" />
          <p className="text-xs font-body text-terracotta uppercase tracking-[0.2em]">After Day 21</p>
        </div>
        <h2 className="font-heading text-3xl md:text-4xl text-forest">Final Reflections</h2>
        <p className="font-body text-stone-600 text-sm md:text-base mt-3 max-w-2xl mx-auto leading-relaxed">
          These reflections are optional, as always — and very helpful if you choose to contemplate this section.
        </p>
      </div>

      <div className="space-y-3">
        {SECTIONS.map((section) => {
          const isOpen = open === section.id;
          return (
            <div key={section.id} className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid={`reflection-${section.id}`}>
              <button
                onClick={() => setOpen(isOpen ? null : section.id)}
                className="w-full px-5 md:px-6 py-4 flex items-center justify-between gap-4 text-left hover:bg-stone-50 transition-colors"
                data-testid={`reflection-toggle-${section.id}`}
              >
                <div>
                  <h3 className="font-heading text-lg md:text-xl text-forest leading-tight">{section.title}</h3>
                  {section.subtitle && <p className="font-accent italic text-terracotta text-sm mt-0.5">{section.subtitle}</p>}
                </div>
                <ChevronDown size={20} className={`text-forest flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="px-5 md:px-6 pb-6">
                      <ul className="space-y-1.5 mb-4 pl-4 border-l-2 border-emerald-200">
                        {section.questions.map((q, i) => (
                          <li key={i} className="font-body text-sm text-stone-700 leading-relaxed">{q}</li>
                        ))}
                      </ul>
                      <textarea
                        value={entries[section.id] || ''}
                        onChange={(e) => setEntry(section.id, e.target.value)}
                        placeholder="Take a moment to reflect and write here…"
                        rows={6}
                        className="w-full rounded-2xl border border-stone-200 bg-stone-50 focus:bg-white focus:border-emerald-400 focus:ring-1 focus:ring-emerald-300 outline-none p-4 font-body text-sm text-stone-700 resize-y transition-colors"
                        data-testid={`reflection-input-${section.id}`}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default FinalReflections;
